import React from "react";
import s from "../labReportPdf.module.scss";

const sectionConfig = {
  "COMPLETE BLOOD COUNT (CBC)": [
    "White Blood Cell(WBC)",
    "Red Blood Cell(RBC)",
    "Haemoglobin",
    "Hematocrit",
    "Mean Corpuscular Volume(MCV)",
    "Mean Corpuscular Haemoglobin(MCH)",
    "Mean Corpuscular Haemoglobin Concentration(MCHC)",
    "RDW-CV",
    "RDW-SD",
    "Platelet Count",
    "MPV",
    "PDW",
    "PCT",
    "P-LCC",
    "P-LCR",
  ],

  "DIFFERENTIAL COUNT(DC)": [
    "Neutrophils",
    "Lymphocytes",
    "Eosinophils",
    "Monocytes",
    "Basophils",
  ],

  "ABSOLUTE COUNT(AC)": [
    "Absolute Neutrophils",
    "Absolute Lymphocytes",
    "Absolute Eosinophils",
    "Absolute Monocytes",
    "Absolute Basophils",
  ],
};

const CBCReport = ({
  profile,
  PageBaseLayout,
  PageFooter,
  patientData,
  userData,
  NABLLOGO,
}) => {
  const patient = patientData || profile?.patientId;
  const user = userData || patient?.userId || profile?.patientId?.userId;
  const getStatusStyle = (value, rangeStr) => {
    if (!value || !rangeStr || rangeStr === "N/A") {
      return {
        fontWeight: "normal",
        color: "#000",
      };
    }

    const numValue = parseFloat(value);

    const limits = String(rangeStr).match(/-?\d+(\.\d+)?/g);

    if (!limits || isNaN(numValue)) {
      return {
        fontWeight: "normal",
        color: "#000",
      };
    }

    const low = parseFloat(limits[0]);
    const high = parseFloat(limits[limits.length - 1]);

    if (numValue < low || numValue > high) {
      return {
        fontWeight: "600",
        color: "#000",
      };
    }

    return {
      fontWeight: "normal",
      color: "#000",
    };
  };
  const chunkArray = (array, size) => {
    const result = [];
    if (!array) return result;
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  const getTestSection = (testName) => {
    for (const [section, tests] of Object.entries(sectionConfig)) {
      if (tests.includes(testName)) {
        return section;
      }
    }
    return null;
  };

  const allSectionTests = Object.keys(sectionConfig)
    .map((section) => {
      const sectionTests =
        profile?.tests?.filter((test) =>
          sectionConfig[section].includes(test.testName),
        ) || [];
      return { title: section, tests: sectionTests };
    })
    .filter((section) => section.tests.length > 0);

  const allTests = allSectionTests.reduce(
    (acc, section) => [...acc, ...section.tests],
    [],
  );

  const testChunks = chunkArray(allTests, 14);

  return (
    <>
      {testChunks.map((chunk, pageIndex) => (
        <div
          key={`cbc-page-${pageIndex}`}
          className={s.reportPage}
          style={{ pageBreakAfter: "always" }}
        >
          <PageBaseLayout
            showSampleType={true}
            currentSampleType={profile?.testSampleType?.name}
          />

          <table className={s.resultsTable}>
            <thead>
              <tr>
                <th style={{ width: "40%" }}>Test Description</th>
                <th style={{ textAlign: "center" }}>Value(s)</th>
                <th style={{ textAlign: "center" }}>Unit(s)</th>
                <th style={{ textAlign: "center" }}>Reference Range</th>
                <th style={{ textAlign: "center", width: "10%" }}>
                  NABL Authorized
                </th>
              </tr>
            </thead>
            <tbody>
              {/* CATEGORY NAME - BELOW THEAD */}
              {pageIndex === 0 && (
                <tr className={s.profileCategoryRow}>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      fontSize: "11px",
                      fontWeight: "900",
                      padding: "8px 8px",
                      color: "#4d4dcf",
                      textTransform: "uppercase",
                      background: "#4fd96d",
                      borderBottom: "none",
                    }}
                  >
                    {profile?.category?.categoryName || "OTHERS"}
                  </td>
                </tr>
              )}
              {chunk.map((test, idx) => {
                const currentSection = getTestSection(test.testName);
                const previousSection =
                  idx > 0 ? getTestSection(chunk[idx - 1].testName) : null;
                const showSectionHeader =
                  currentSection && currentSection !== previousSection;

                return (
                  <React.Fragment key={idx}>
                    {showSectionHeader && (
                      <tr className={s.sectionHeaderRow}>
                        <td
                          colSpan={5}
                          style={{
                            textAlign: "left",
                            fontSize: "11px",
                            fontWeight: "700",
                            padding: "4px 8px",
                            color: "#4d4dcf",
                            background: "#fff",
                            borderBottom: "1px solid #ddd",
                          }}
                        >
                          {currentSection}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ paddingLeft: "15px", borderBottom: "none" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            flexWrap: "wrap",
                            gap: "6px",
                          }}
                        >
                          <strong>{test.testName}</strong>
                          {test?.testMethod && (
                            <span
                              style={{
                                fontSize: "8px",
                                color: "#666",
                                fontStyle: "italic",
                              }}
                            >
                              ({test?.testMethod?.name})
                            </span>
                          )}
                        </div>
                      </td>
                      <td
                        align="center"
                        style={{
                          borderBottom: "none",
                          ...getStatusStyle(
                            test.value,
                            (() => {
                              const age =
                                user?.age?.year ?? patient?.age?.year ?? 0;
                              const gender =
                                `${user?.gender || patient?.userId?.gender || ""}`
                                  .trim()
                                  .toLowerCase();

                              if (
                                age < 10 &&
                                test.ilow_range &&
                                test.ihigh_range
                              ) {
                                return `${test.ilow_range} - ${test.ihigh_range}`;
                              }

                              if (
                                age >= 10 &&
                                gender === "male" &&
                                test.mlow_range &&
                                test.mhigh_range
                              ) {
                                return `${test.mlow_range} - ${test.mhigh_range}`;
                              }

                              if (
                                age >= 10 &&
                                gender === "female" &&
                                test.flow_range &&
                                test.fhigh_range
                              ) {
                                return `${test.flow_range} - ${test.fhigh_range}`;
                              }

                              if (test.low_range && test.high_range) {
                                return `${test.low_range} - ${test.high_range}`;
                              }

                              return (
                                test.reference_range_details ||
                                test.reference_range ||
                                "N/A"
                              );
                            })(),
                          ),
                        }}
                      >
                        {test.value || "-"}
                      </td>
                      <td align="center" style={{ borderBottom: "none" }}>
                        {test.units || "-"}
                      </td>
                      <td align="center" style={{ borderBottom: "none" }}>
                        {(() => {
                          const age =
                            user?.age?.year ?? patient?.age?.year ?? 0;
                          const gender =
                            `${user?.gender || patient?.userId?.gender || ""}`
                              .trim()
                              .toLowerCase();

                          if (age < 10 && test.ilow_range && test.ihigh_range) {
                            return `${test.ilow_range} - ${test.ihigh_range}`;
                          }

                          if (
                            age >= 10 &&
                            gender === "male" &&
                            test.mlow_range &&
                            test.mhigh_range
                          ) {
                            return `${test.mlow_range} - ${test.mhigh_range}`;
                          }

                          if (
                            age >= 10 &&
                            gender === "female" &&
                            test.flow_range &&
                            test.fhigh_range
                          ) {
                            return `${test.flow_range} - ${test.fhigh_range}`;
                          }

                          if (test.low_range && test.high_range) {
                            return `${test.low_range} - ${test.high_range}`;
                          }

                          return (
                            test.reference_range_details ||
                            test.reference_range ||
                            "N/A"
                          );
                        })()}
                      </td>
                      <td align="center" style={{ borderBottom: "none" }}>
                        {test?.isNABL === "Yes" ? (
                          <img
                            src={NABLLOGO}
                            alt="NABL Approved"
                            title="NABL Approved"
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <PageFooter />
        </div>
      ))}
    </>
  );
};

export default CBCReport;
